import { z } from 'zod'
import { moneySchema } from './common.js'
import type { AccountRole } from './ledger.js'
import type { Permission } from './permissions.js'

/**
 * Books health (AA1; docs/refactor/ACCOUNTING_ASSISTANT.md §3.1): rule-based checks that find mistakes and loose
 * ends in the books for a period. No AI and no data leaving the company: the accounting assistant (ai.ts,
 * `accounting.review`) explains and ranks these findings, it never decides what is wrong.
 */

const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)
const days = (d: number) => z.number().int().min(0).max(365).default(d)
const pct = (d: number) => z.number().int().min(0).max(100).default(d)
const amount = (d: string) => moneySchema.refine((v) => Number(v) >= 0, 'Cannot be negative').default(d)

export const BOOK_CHECK_SEVERITIES = ['info', 'warning', 'critical'] as const
export type BookCheckSeverity = (typeof BOOK_CHECK_SEVERITIES)[number]

export const BOOK_CHECK_AREAS = ['purchasing', 'expenses', 'bank', 'ledger', 'vat', 'sales', 'setup'] as const
export type BookCheckArea = (typeof BOOK_CHECK_AREAS)[number]

export const BOOK_CHECK_CODES = [
  'DUPLICATE_SUPPLIER_BILL',
  'DUPLICATE_EXPENSE',
  'BANK_NOT_RECONCILED',
  'WRONG_SIDE_BALANCE',
  'NEGATIVE_CASH',
  'OPENING_BALANCE_NOT_CLEARED',
  'VAT_DIFFERENCE',
  'BALANCE_SHEET_UNBALANCED',
  'SUBLEDGER_DIFFERENCE',
  'LARGE_MANUAL_JOURNAL',
  'BACKDATED_ENTRY',
  'OLD_DRAFTS',
  'ROLE_NOT_MAPPED',
  'RECEIVABLES_OVERDUE',
  'CUSTOMER_CREDIT_BALANCES',
  'UNCATEGORISED_SPEND',
  'PERIOD_NOT_LOCKED',
] as const
export type BookCheckCode = (typeof BOOK_CHECK_CODES)[number]

export interface BookCheckDef {
  area: BookCheckArea
  /** The severity it raises (BANK_NOT_RECONCILED turns critical at twice its threshold). */
  severity: BookCheckSeverity
  /** A reader needs one of these to see its findings (and the Books health page needs ledger.read or reports.financial). */
  permissions: readonly Permission[]
  /** The `{{vars}}` its EN/AR texts use (a locale test compares them). */
  params: readonly string[]
  /** Where its action goes (the web resolves the route; refs carry the records). */
  action:
    | 'purchases'
    | 'expenses'
    | 'bank'
    | 'account'
    | 'vat'
    | 'balance_sheet'
    | 'journal'
    | 'drafts'
    | 'mapping'
    | 'aging'
    | 'customer'
    | 'lock_date'
}

const ledger: readonly Permission[] = ['ledger.read', 'reports.financial']

/**
 * SUBLEDGER_DIFFERENCE: the control accounts tied to their records at the period end, in its order:
 * receivables to the customer balances, payables to the supplier balances, inventory to the stock
 * valuation, and the cheque clearing accounts to the open cheques of the register.
 */
export const SUBLEDGER_CHECK_ROLES = [
  'receivables',
  'payables',
  'inventory',
  'cheques_receivable',
  'cheques_payable',
] as const satisfies readonly AccountRole[]
export type SubledgerCheckRole = (typeof SUBLEDGER_CHECK_ROLES)[number]

export const BOOK_CHECK_DEFS: Record<BookCheckCode, BookCheckDef> = {
  DUPLICATE_SUPPLIER_BILL: {
    area: 'purchasing',
    severity: 'warning',
    permissions: ['purchasing.read'],
    params: ['supplier', 'total', 'count', 'reference'],
    action: 'purchases',
  },
  DUPLICATE_EXPENSE: {
    area: 'expenses',
    severity: 'warning',
    permissions: ['expenses.read'],
    params: ['payee', 'total', 'count'],
    action: 'expenses',
  },
  BANK_NOT_RECONCILED: {
    area: 'bank',
    severity: 'warning',
    permissions: ['treasury.read'],
    params: ['account', 'accountAr', 'accountCode', 'days', 'lastReconciledOn', 'unmatched'],
    action: 'bank',
  },
  WRONG_SIDE_BALANCE: {
    area: 'ledger',
    severity: 'warning',
    permissions: ledger,
    params: ['account', 'accountAr', 'accountCode', 'balance', 'side'],
    action: 'account',
  },
  NEGATIVE_CASH: {
    area: 'bank',
    severity: 'critical',
    permissions: ledger,
    params: ['account', 'accountAr', 'accountCode', 'balance'],
    action: 'account',
  },
  OPENING_BALANCE_NOT_CLEARED: {
    area: 'ledger',
    severity: 'warning',
    permissions: ledger,
    params: ['account', 'accountAr', 'accountCode', 'balance'],
    action: 'account',
  },
  VAT_DIFFERENCE: {
    area: 'vat',
    severity: 'critical',
    permissions: ledger,
    params: ['difference'],
    action: 'vat',
  },
  BALANCE_SHEET_UNBALANCED: {
    area: 'ledger',
    severity: 'critical',
    permissions: ledger,
    params: ['difference'],
    action: 'balance_sheet',
  },
  /**
   * A control account (SUBLEDGER_CHECK_ROLES) differs from its records at the period end: `subledger` is
   * the role, `balance` the account's balance on its own side, `difference` the balance less the records.
   */
  SUBLEDGER_DIFFERENCE: {
    area: 'ledger',
    severity: 'critical',
    permissions: ledger,
    params: ['account', 'accountAr', 'accountCode', 'subledger', 'balance', 'difference'],
    action: 'account',
  },
  LARGE_MANUAL_JOURNAL: {
    area: 'ledger',
    severity: 'info',
    permissions: ledger,
    params: ['number', 'total', 'threshold'],
    action: 'journal',
  },
  BACKDATED_ENTRY: {
    area: 'ledger',
    severity: 'warning',
    permissions: ledger,
    params: ['number', 'entryDate', 'postedOn', 'days'],
    action: 'journal',
  },
  OLD_DRAFTS: {
    area: 'setup',
    severity: 'info',
    permissions: ['sales.read', 'purchasing.read'],
    params: ['count', 'oldestOn', 'kind'],
    action: 'drafts',
  },
  ROLE_NOT_MAPPED: {
    area: 'setup',
    severity: 'warning',
    permissions: ledger,
    params: ['role'],
    action: 'mapping',
  },
  RECEIVABLES_OVERDUE: {
    area: 'sales',
    severity: 'warning',
    permissions: ['sales.read'],
    params: ['overdue', 'total', 'pct'],
    action: 'aging',
  },
  CUSTOMER_CREDIT_BALANCES: {
    area: 'sales',
    severity: 'info',
    permissions: ['sales.read'],
    params: ['customer', 'credit'],
    action: 'customer',
  },
  UNCATEGORISED_SPEND: {
    area: 'expenses',
    severity: 'info',
    permissions: ['expenses.read', ...ledger],
    params: ['amount', 'pct'],
    action: 'expenses',
  },
  PERIOD_NOT_LOCKED: {
    area: 'setup',
    severity: 'info',
    permissions: ledger,
    params: ['lockDate', 'suggested'],
    action: 'lock_date',
  },
}

// ── settings (group `accountingChecks`: read ledger.read, write settings.manage) ─────────────────────

export const accountingChecksSettingsSchema = z.object({
  /** Each check on (default) or off. */
  enabled: z
    .object(
      Object.fromEntries(BOOK_CHECK_CODES.map((c) => [c, z.boolean().default(true)])) as Record<
        BookCheckCode,
        z.ZodDefault<z.ZodBoolean>
      >,
    )
    .default(Object.fromEntries(BOOK_CHECK_CODES.map((c) => [c, true])) as Record<BookCheckCode, boolean>),
  /** DUPLICATE_SUPPLIER_BILL, DUPLICATE_EXPENSE: the same amount within this many days. */
  duplicateWindowDays: days(3),
  /** BANK_NOT_RECONCILED: days since the last completed statement (critical at twice). */
  bankStaleDays: days(35),
  /** WRONG_SIDE_BALANCE, OPENING_BALANCE_NOT_CLEARED: from this amount. */
  wrongSideMin: amount('1.000'),
  openingBalanceMin: amount('1.000'),
  /** VAT_DIFFERENCE: from this amount. */
  vatDifferenceMin: amount('0.001'),
  /** LARGE_MANUAL_JOURNAL: manual journals from this total. */
  largeManualJournal: amount('5000.000'),
  /** BACKDATED_ENTRY: dated more than this many days before posting. */
  backdatedDays: days(30),
  /** OLD_DRAFTS: drafts older than this many days. */
  oldDraftDays: days(14),
  /** RECEIVABLES_OVERDUE: over-90-day receivables above this share. */
  receivablesOverduePct: pct(20),
  /** CUSTOMER_CREDIT_BALANCES: credits from this amount. */
  customerCreditMin: amount('100.000'),
  /** UNCATEGORISED_SPEND: general operating expense above this share of the period's spend. */
  uncategorisedPct: pct(15),
})
export type AccountingChecksSettings = z.output<typeof accountingChecksSettingsSchema>
export const DEFAULT_ACCOUNTING_CHECKS_SETTINGS: AccountingChecksSettings =
  accountingChecksSettingsSchema.parse({})

// ── findings (`GET /accounting/health?from&to`) ───────────────────────────────────────────────────────

export const booksHealthQuerySchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    includeDismissed: z.stringbool().optional(),
  })
  .refine((v) => v.to >= v.from, { path: ['to'], message: 'The end is before the start' })
export type BooksHealthQuery = z.input<typeof booksHealthQuerySchema>

export type BookFindingParam = string | number | null

export interface BookFinding {
  code: BookCheckCode
  severity: BookCheckSeverity
  area: BookCheckArea
  /** What inside the books it is about (a document pair, an account, a customer): stable across runs. */
  scopeKey: string
  /**
   * An account a finding names comes as `account` (its English name), `accountAr` (its Arabic name, or null)
   * and `accountCode` (the chart code; a bank's own account's code), so the page reads it in its language.
   */
  params: Record<string, BookFindingParam>
  /**
   * The records behind it, each only if the reader may open it. A draft (OLD_DRAFTS) has no number: its
   * `date` (`YYYY-MM-DD`, the document's own date) comes apart, beside its kind (`type`), to label it with;
   * `label` is then its supplier reference, or that date. Optional for fixtures.
   */
  refs: { type: string; id: string; label: string; date?: string }[]
  /** Since when it holds, when known. */
  since: string | null
  /** A "not a problem" with its reason; dismissed findings are listed only with `includeDismissed`. */
  dismissed: {
    by: { id: string; name: string; nameAr: string | null } | null
    reason: string
    /** null: for this item, for good. */
    untilOn: string | null
    at: string
  } | null
}

export interface BooksHealth {
  from: string
  to: string
  computedAt: string
  /** Worst first, then by code. */
  findings: BookFinding[]
  /** Counts of the findings not dismissed. */
  counts: Record<BookCheckSeverity, number>
  dismissedCount: number
  /** Checks that didn't run for this reader, and why. */
  /** `failed`: the check couldn't run this time (an error or its time limit); the others still did. */
  skipped: { code: BookCheckCode; reason: 'disabled' | 'no_permission' | 'module_off' | 'failed' }[]
  /**
   * Checks with more findings than listed (at most 200 each): counts stay exact, the list is cut. Optional for
   * fixtures.
   */
  truncated?: { code: BookCheckCode; shown: number; total: number }[]
  /** Whether the reader may dismiss findings (journals.post or accounts.manage). */
  can: { dismiss: boolean }
}

/** `POST /accounting/health/dismissals`: "not a problem" (journals.post or accounts.manage). */
export const bookFindingDismissSchema = z.object({
  code: z.enum(BOOK_CHECK_CODES),
  scopeKey: requiredText(200),
  reason: requiredText(500, 'Say why it is not a problem'),
  /** At most 180 days ahead; null: for this item, for good. */
  untilOn: z.iso
    .date()
    .nullish()
    .transform((v) => v ?? null),
})
export type BookFindingDismissInput = z.input<typeof bookFindingDismissSchema>
/** At most this many days ahead for a dismissal's end. */
export const BOOK_DISMISSAL_MAX_DAYS = 180
/** `DELETE /accounting/health/dismissals?code&scopeKey` ends a dismissal (204). */
export const bookFindingUndismissQuerySchema = z.object({
  code: z.enum(BOOK_CHECK_CODES),
  scopeKey: requiredText(200),
})

// ── the rule-based drill-down (`GET /accounting/drilldown`) ──────────────────────────────────────────

export const DRILLDOWN_REPORTS = [
  'profit_and_loss',
  'balance_sheet',
  'trial_balance',
  'cash_flow',
  'vat_return',
] as const
export type DrilldownReport = (typeof DRILLDOWN_REPORTS)[number]

/**
 * The biggest movements behind a report figure (reports.financial or ledger.read): an account (`accountId`), or a
 * report section (`section`, the key the report itself returns), for a period (`from`/`to`) or a date (`asOf`,
 * balance sheet), with an optional comparison period. Works without AI; "Explain" builds on it.
 */
export const drilldownQuerySchema = z
  .object({
    report: z.enum(DRILLDOWN_REPORTS),
    accountId: z.uuid().optional(),
    section: z.string().trim().min(1).max(60).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    asOf: z.iso.date().optional(),
    compareFrom: z.iso.date().optional(),
    compareTo: z.iso.date().optional(),
  })
  .refine((v) => (v.accountId === undefined) !== (v.section === undefined), {
    path: ['accountId'],
    message: 'Name either an account or a section',
  })
export type DrilldownQuery = z.input<typeof drilldownQuerySchema>

export interface DrilldownMovement {
  /** The document or journal entry behind it, if the reader may open it. */
  source: { type: string; id: string; number: string | null; date: string; label: string } | null
  description: string | null
  amount: string
  /** The same source's amount in the comparison period, when compared. */
  compareAmount: string | null
  /**
   * The customer its source document belongs to (sales documents), whatever the reader may open: the
   * assistant cites it, so anonymising the customer purges the conversation. Optional for fixtures.
   */
  customerId?: string | null
}

export interface Drilldown {
  report: DrilldownReport
  /** The account or section, in the reader's terms. */
  label: { en: string; ar: string | null; code: string | null }
  period: { from: string | null; to: string | null; asOf: string | null }
  total: string
  compareTotal: string | null
  /** total − compareTotal. */
  change: string | null
  /** The 20 biggest movements (by absolute amount), then the rest as one amount. */
  movements: DrilldownMovement[]
  otherAmount: string
  /** For a section: its accounts and their amounts, biggest first. */
  accounts: {
    id: string
    code: string
    name: string
    nameAr: string | null
    amount: string
    compareAmount: string | null
  }[]
}

// ── the weekly summary and errors ─────────────────────────────────────────────────────────────────────

/** `BOOKS_HEALTH_DIGEST` notification data. */
export interface BooksHealthDigestData {
  from: string
  to: string
  critical: number
  warning: number
  info: number
}

export const BOOKS_HEALTH_ERROR_CODES = [
  /** 422: dismissing a finding that doesn't hold now. */
  'FINDING_NOT_ACTIVE',
  /** 422: a dismissal's end more than 180 days ahead, or in the past. */
  'DISMISSAL_DATE',
  /** 422: a Books health period longer than a year. */
  'HEALTH_PERIOD',
  /** 409: someone dismissed the same finding at the same moment. */
  'DISMISSAL_CONFLICT',
  /** 503: the drill-down, or a dismissal's check, took longer than its time limit (try a shorter period). */
  'BOOKS_READ_TIMEOUT',
  /** 503: Books health is not reachable by the assistant (its tools answer this to the model). */
  'BOOKS_HEALTH_UNAVAILABLE',
] as const
export type BooksHealthErrorCode = (typeof BOOKS_HEALTH_ERROR_CODES)[number]

// ── the lock-date warning (`GET /accounting/lock-date/warning?lockDate`, ledger.read) ───────────────

/** The lock date being proposed: the warning covers its month, from the 1st to that date. */
export const lockDateWarningQuerySchema = z.object({ lockDate: z.iso.date() })
export type LockDateWarningQuery = z.input<typeof lockDateWarningQuerySchema>
/**
 * The reader's open (not dismissed) findings of the month a proposed lock date would close, by severity. The
 * lock-date page warns, never blocks, with warning or critical findings (§3.1).
 */
export type LockDateWarning = BooksHealthDigestData
